import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Download, MapPin, Calendar, Sparkles } from "lucide-react";
import BrochureDialog from "@/components/BrochureDialog";
import { trackEvent } from "@/lib/analytics";

const CPS = {
    slug: "cps-2026",
    name: "Climate Power Summit 2026",
    location: "Jaipur, India",
    dates: "13–15 November 2026",
    tagline: "People • Planet • Progress",
    status: "Applications Now Open",
    img: "https://images.unsplash.com/photo-1757310062384-d3bcfe3026e8?w=1600&q=80&fm=jpg&fit=crop",
    brochure: "https://customer-assets-v7afamib.emergentagent.net/job_f4c7dbec-80a4-4d4b-81c9-de1d792e1fe8/artifacts/yudn4mgt_CPS%20.pdf",
    opportunities: [
        { count: "10", label: "Fully Funded" },
        { count: "40", label: "Partially Funded" },
        { count: "20", label: "Self-Funded" },
    ],
};

/* Deep-green / emerald / cream Climate Power Summit visual identity */
const CPS_STYLE = {
    "--cps-forest": "#0F2A22",
    "--cps-emerald": "#1F6E4C",
    "--cps-lime": "#A7C957",
    "--cps-cream": "#F5EFD8",
    "--cps-cream-dim": "rgba(245,239,216,0.72)",
};

export default function SignaturePrograms() {
    const [brochure, setBrochure] = useState(null);
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], [40, -40]);

    return (
        <section
            data-testid="programs-section"
            style={CPS_STYLE}
            className="relative py-24 md:py-40 overflow-hidden"
        >
            {/* CPS deep-green background with subtle texture */}
            <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, #0F2A22 0%, #143A2C 60%, #0F2A22 100%)" }} />
            <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #A7C957 0, transparent 40%), radial-gradient(circle at 80% 80%, #1F6E4C 0, transparent 50%)" }} />

            <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24">
                {/* Section eyebrow + headline */}
                <div className="grid md:grid-cols-12 gap-10 mb-16 md:mb-24">
                    <div className="md:col-span-4">
                        <div className="label-mono" style={{ color: "var(--cps-lime)" }}>— SIGNATURE PROGRAM · 2026</div>
                    </div>
                    <div className="md:col-span-8">
                        <h2 className="serif-display text-4xl md:text-6xl lg:text-7xl" style={{ color: "var(--cps-cream)" }}>
                            One flagship experience.
                            <span className="serif-italic block" style={{ color: "var(--cps-lime)" }}>
                                One movement.
                            </span>
                        </h2>
                    </div>
                </div>

                {/* CPS Card */}
                <motion.article
                    ref={ref}
                    data-testid="program-card-cps-2026"
                    initial={{ opacity: 0, y: 60 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
                    className="grid md:grid-cols-12 gap-8 md:gap-14 items-start"
                >
                    {/* Text column */}
                    <div className="md:col-span-6 order-2 md:order-1">
                        <div className="flex items-center gap-4 mb-6">
                            <span
                                className="inline-flex items-center gap-2 px-3 py-1.5 label-mono text-[10px]"
                                style={{
                                    background: "rgba(167,201,87,0.14)",
                                    color: "var(--cps-lime)",
                                    border: "1px solid rgba(167,201,87,0.4)",
                                }}
                                data-testid="cps-status-badge"
                            >
                                <Sparkles className="w-3 h-3" /> {CPS.status}
                            </span>
                        </div>

                        <h3
                            className="serif-display text-4xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight"
                            style={{ color: "var(--cps-cream)" }}
                        >
                            Climate Power<br />
                            <span className="serif-italic" style={{ color: "var(--cps-lime)" }}>Summit 2026</span>
                            <span style={{ color: "var(--cps-lime)" }}>.</span>
                        </h3>

                        <p className="mt-5 serif-italic text-xl md:text-2xl" style={{ color: "var(--cps-lime)" }}>
                            {CPS.tagline}
                        </p>

                        <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3">
                            <div className="flex items-center gap-2 label-mono" style={{ color: "var(--cps-cream-dim)" }}>
                                <Calendar className="w-3.5 h-3.5" style={{ color: "var(--cps-lime)" }} />
                                {CPS.dates}
                            </div>
                            <div className="flex items-center gap-2 label-mono" style={{ color: "var(--cps-cream-dim)" }}>
                                <MapPin className="w-3.5 h-3.5" style={{ color: "var(--cps-lime)" }} />
                                {CPS.location}
                            </div>
                        </div>

                        {/* Funding opportunities */}
                        <div
                            className="mt-10 grid grid-cols-3 gap-0 border"
                            style={{ borderColor: "rgba(245,239,216,0.18)" }}
                            data-testid="cps-opportunities"
                        >
                            {CPS.opportunities.map((op, i) => (
                                <div
                                    key={op.label}
                                    className={`p-5 ${i < 2 ? "border-r" : ""}`}
                                    style={{ borderColor: "rgba(245,239,216,0.18)" }}
                                    data-testid={`cps-opp-${op.label.toLowerCase().replace(/\s+/g, "-")}`}
                                >
                                    <div
                                        className="serif-display text-3xl md:text-4xl"
                                        style={{ color: "var(--cps-lime)" }}
                                    >
                                        {op.count}
                                    </div>
                                    <div
                                        className="label-mono text-[10px] mt-1"
                                        style={{ color: "var(--cps-cream-dim)" }}
                                    >
                                        {op.label}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <p
                            className="mt-4 text-sm label-mono"
                            style={{ color: "var(--cps-cream-dim)" }}
                        >
                            Funding category is assigned after application review
                        </p>

                        {/* CTAs */}
                        <div className="mt-10 flex flex-wrap gap-3">
                            <Link
                                to="/apply/cps-2026"
                                data-testid="cps-apply-now-btn"
                                onClick={() => trackEvent("CPS_Apply_Click", { source: "home_signature_programs" })}
                                className="inline-flex items-center gap-2 px-6 py-3.5 label-mono text-[11px] font-medium transition-all hover:scale-[1.02]"
                                style={{
                                    background: "var(--cps-lime)",
                                    color: "var(--cps-forest)",
                                }}
                            >
                                Apply Now <ArrowUpRight className="w-4 h-4" />
                            </Link>
                            <Link
                                to="/programs/cps-2026"
                                data-testid="cps-explore-summit-btn"
                                className="inline-flex items-center gap-2 px-6 py-3.5 label-mono text-[11px] font-medium transition-all hover:bg-[rgba(167,201,87,0.1)]"
                                style={{
                                    border: "1px solid var(--cps-lime)",
                                    color: "var(--cps-cream)",
                                }}
                            >
                                Explore the Summit <ArrowUpRight className="w-4 h-4" />
                            </Link>
                            <button
                                type="button"
                                onClick={() => setBrochure({ url: CPS.brochure, program_slug: CPS.slug, program_name: CPS.name })}
                                data-testid="cps-brochure-btn"
                                className="inline-flex items-center gap-2 px-6 py-3.5 label-mono text-[11px] transition-all hover:bg-[rgba(245,239,216,0.06)]"
                                style={{
                                    border: "1px solid rgba(245,239,216,0.24)",
                                    color: "var(--cps-cream-dim)",
                                }}
                            >
                                Download Brochure <Download className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    {/* Image column */}
                    <div className="md:col-span-6 order-1 md:order-2 relative">
                        <div className="relative aspect-[4/5] md:aspect-[3/4] w-full overflow-hidden">
                            <motion.img
                                style={{ y }}
                                src={CPS.img}
                                alt="Climate Power Summit 2026 — Jaipur, India"
                                className="w-full h-full object-cover scale-110"
                            />
                            <div
                                className="absolute inset-0"
                                style={{ background: "linear-gradient(180deg, transparent 50%, rgba(15,42,34,0.75) 100%)" }}
                            />
                            {/* Bottom-left location plate */}
                            <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between gap-4">
                                <div>
                                    <div className="label-mono text-[10px]" style={{ color: "var(--cps-lime)" }}>
                                        HOSTED IN
                                    </div>
                                    <div className="serif-display text-2xl md:text-3xl mt-1" style={{ color: "var(--cps-cream)" }}>
                                        Jaipur, India
                                    </div>
                                </div>
                                <div
                                    className="px-3 py-2 label-mono text-[10px]"
                                    style={{ background: "rgba(15,42,34,0.7)", color: "var(--cps-cream)", border: "1px solid rgba(167,201,87,0.35)" }}
                                >
                                    13–15 NOV 2026
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.article>
            </div>
            <BrochureDialog brochure={brochure} onClose={() => setBrochure(null)} />
        </section>
    );
}
